import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Mail, Receipt, ShoppingCart, Undo2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cartFromRequest, cartSummary, checkoutBody, historyCsv, METHOD_LABELS, money, selectAllAvailable, setLineAmount, toggleLine } from './disbursementsModel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 11/03 (Josh): "a shopping cart option, where its select which deal is getting paid off, and 'checkout', this
// generates a receipt which can be emailed to the client at my discretion … saving the data … they can view it in
// their syndicator portal." Admin page. Every line becomes an Approved withdrawal on that position, so the deal page,
// Distributions and the investor portal all move by the same amount.
export default function SyndicationDisbursements({ embedded = false }) {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders } = useAuth();
  const [params, setParams] = useSearchParams();
  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);

  const [syndicators, setSyndicators] = useState([]);
  const [search, setSearch] = useState('');
  const syndicatorId = params.get('syndicator') || '';
  const requestId = params.get('request') || '';

  const [cartData, setCartData] = useState(null);
  const [cart, setCart] = useState({});
  const [method, setMethod] = useState('wire');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // dry-run preview awaiting Checkout
  const [receipt, setReceipt] = useState(null); // the disbursement just booked / opened

  const [history, setHistory] = useState({ rows: [], summary: null });
  const [histFilter, setHistFilter] = useState({ from: '', to: '' });
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  useEffect(() => {
    axios.get(`${API}/syndications/fee-ledger/syndicators`, { headers: headers() })
      .then(r => setSyndicators(r.data || [])).catch(() => toast.error('Could not load the syndicator list'));
  }, [headers]);

  const loadCart = useCallback(async () => {
    if (!syndicatorId) { setCartData(null); setCart({}); return; }
    try {
      const r = await axios.get(`${API}/disbursements/cart/${syndicatorId}`, { headers: headers() });
      setCartData(r.data);
      const req = requestId ? (r.data.open_requests || []).find(x => x.id === requestId) : null;
      setCart(req ? cartFromRequest(req, r.data.positions) : {});
      if (req?.note) setNote(req.note);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the cart'); }
  }, [syndicatorId, requestId, headers]);
  useEffect(() => { loadCart(); }, [loadCart]);

  const loadHistory = useCallback(async () => {
    const q = new URLSearchParams();
    if (syndicatorId) q.set('syndicator_id', syndicatorId);
    if (histFilter.from) q.set('date_from', histFilter.from);
    if (histFilter.to) q.set('date_to', histFilter.to);
    try {
      const r = await axios.get(`${API}/disbursements?${q}`, { headers: headers() });
      setHistory(r.data);
    } catch (e) { toast.error('Could not load disbursement history'); }
  }, [syndicatorId, histFilter, headers]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const positions = cartData?.positions || [];
  const summary = useMemo(() => cartSummary(cart, positions), [cart, positions]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? syndicators.filter(s => `${s.company_name || ''} ${s.name || ''}`.toLowerCase().includes(q)) : syndicators;
  }, [search, syndicators]);

  const pick = (id) => { const p = new URLSearchParams(params); if (id) p.set('syndicator', id); else p.delete('syndicator'); p.delete('request'); setParams(p); setSearch(''); };

  const body = (dryRun) => checkoutBody(cart, positions, { syndicatorId, method, reference, note, paidOn, requestId, dryRun });

  const preview = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/disbursements/checkout`, body(true), { headers: headers() });
      setConfirm(r.data.disbursement);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.errors ? d.errors.join(' · ') : (typeof d === 'string' ? d : 'The cart was refused'));
    } finally { setBusy(false); }
  };
  const doCheckout = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/disbursements/checkout`, body(false), { headers: headers() });
      toast.success(r.data.message);
      (r.data.disbursement.recompute_warnings || []).forEach(w => toast.warning(`Recompute of ${w.deal_id} failed: ${w.error}`));
      setConfirm(null); setReceipt(r.data.disbursement); setCart({}); setReference(''); setNote('');
      if (requestId) { const p = new URLSearchParams(params); p.delete('request'); setParams(p); }
      await loadCart(); await loadHistory();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.errors ? d.errors.join(' · ') : (typeof d === 'string' ? d : 'Checkout failed'));
    } finally { setBusy(false); }
  };
  const openPdf = async (d) => {
    try {
      const r = await axios.get(`${API}/disbursements/${d.id}/receipt.pdf`, { headers: headers(), responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
    } catch { toast.error('Could not render the receipt'); }
  };
  const sendEmail = async () => {
    if (!emailTarget) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/disbursements/${emailTarget.id}/email`, { to: emailTo || null }, { headers: headers() });
      toast.success(r.data.message); setEmailTarget(null); await loadHistory();
      if (receipt?.id === emailTarget.id) setReceipt({ ...receipt, receipt_emailed_at: r.data.emailed_at, receipt_emailed_to: r.data.to, receipt_email_count: (receipt.receipt_email_count || 0) + 1 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Email failed'); }
    finally { setBusy(false); }
  };
  const doVoid = async () => {
    if (!voidTarget) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/disbursements/${voidTarget.id}/void`, { reason: voidReason }, { headers: headers() });
      toast.success(r.data.message); setVoidTarget(null); setVoidReason('');
      if (receipt?.id === voidTarget.id) setReceipt(null);
      await loadCart(); await loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
    finally { setBusy(false); }
  };
  const downloadCsv = () => {
    const blob = new Blob([historyCsv(history.rows)], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'disbursements.csv'; a.click();
  };

  const selected = syndicators.find(s => s.id === syndicatorId);
  const openRequest = requestId && (cartData?.open_requests || []).find(x => x.id === requestId);

  return (
    <Wrap>
      <div className="space-y-6" data-testid="disbursements-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShoppingCart className="h-6 w-6" /> Disbursements</h1>
            <p className="text-sm text-slate-500 mt-1">Pick a syndicator, tick the deals being paid, check out. Every line is deducted from that deal's position and shows in their portal with the same receipt.</p>
          </div>
          <Link to="/syndication-dashboard" className="text-sm text-blue-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Distributions</Link>
        </div>

        {/* Syndicator picker */}
        <Card>
          <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 max-w-md">
              <Label className="text-xs text-slate-500">Syndicator</Label>
              {selected ? (
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{selected.company_name || selected.name}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => pick('')}>change</Button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Type a syndicator name…" data-testid="disb-syndicator-search" />
                  {search && (
                    <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-white shadow">
                      {filtered.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No match</div>}
                      {filtered.slice(0, 40).map(s => (
                        <button key={s.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => pick(s.id)}>
                          {s.company_name || s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {cartData && (
              <div className="text-right">
                <div className="text-xs text-slate-500">Available to pay today</div>
                <div className="text-2xl font-bold text-emerald-700" data-testid="disb-available-total">{money(cartData.available_total)}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {cartData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Positions = the shelves */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Deals with money available</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCart(selectAllAvailable(positions))}>Select all available</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setCart({})}>Clear</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {openRequest && (
                  <div className="mx-4 mb-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                    Settling request of <b>{money(openRequest.amount)}</b> from {new Date(openRequest.created_at).toLocaleDateString()}{openRequest.note ? ` — "${openRequest.note}"` : ''}. The cart is pre-filled; adjust and check out.
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Deal</TableHead>
                        <TableHead className="text-right">Collected share</TableHead>
                        <TableHead className="text-right">Withdrawn</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                        <TableHead className="text-right w-40">Pay now</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {positions.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No positions.</TableCell></TableRow>}
                      {positions.map(p => {
                        const inCart = p.position_id in cart;
                        const line = summary.lines.find(l => l.position_id === p.position_id);
                        return (
                          <TableRow key={p.position_id} className={inCart ? 'bg-emerald-50/50' : p.available <= 0 ? 'opacity-60' : ''} data-testid={`disb-row-${p.deal_number}`}>
                            <TableCell><input type="checkbox" checked={inCart} disabled={p.available <= 0 && !inCart} onChange={() => setCart(toggleLine(cart, p))} aria-label={`select ${p.deal_number}`} /></TableCell>
                            <TableCell>
                              <Link to={`/deals/${p.deal_id}`} className="text-blue-600 hover:underline font-medium">{p.deal_number}</Link>
                              <span className="ml-2 text-slate-700">{p.deal_name}</span>
                              {p.deal_status && <Badge variant="outline" className="ml-2 text-[10px]">{p.deal_status}</Badge>}
                            </TableCell>
                            <TableCell className="text-right text-slate-600">{money(p.collected_share)}</TableCell>
                            <TableCell className="text-right text-orange-700">{money(p.withdrawn)}</TableCell>
                            <TableCell className={`text-right font-semibold ${p.available > 0 ? 'text-emerald-700' : p.available < 0 ? 'text-red-600' : 'text-slate-500'}`}>{money(p.available)}</TableCell>
                            <TableCell className="text-right">
                              {inCart && <Input value={cart[p.position_id]} onChange={e => setCart(setLineAmount(cart, p.position_id, e.target.value))}
                                                className={`h-8 text-right ${line?.over || line?.invalid ? 'border-red-500' : ''}`} inputMode="decimal" />}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Checkout */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" /> Checkout</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-500">{summary.count} deal{summary.count === 1 ? '' : 's'} in the cart</span>
                  <span className="text-2xl font-bold" data-testid="disb-cart-total">{money(summary.total)}</span>
                </div>
                {summary.problems > 0 && <p className="text-xs text-red-600">{summary.problems} line{summary.problems === 1 ? '' : 's'} over the available amount or not a number.</p>}
                <div>
                  <Label className="text-xs">Paid on</Label>
                  <Input type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)} className="h-8" />
                </div>
                <div>
                  <Label className="text-xs">Method</Label>
                  <select value={method} onChange={e => setMethod(e.target.value)} className="mt-1 w-full h-8 rounded border px-2 text-sm">
                    {(cartData.methods || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Reference (wire #, check #)</Label>
                  <Input value={reference} onChange={e => setReference(e.target.value)} className="h-8" placeholder="optional" />
                </div>
                <div>
                  <Label className="text-xs">Reason / note (saved on the receipt and the syndicator's record)</Label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="e.g. Weekly payout 11/03" />
                </div>
                <Button className="w-full" disabled={!summary.ready || busy} onClick={preview} data-testid="disb-checkout">
                  <ShoppingCart className="h-4 w-4 mr-2" /> Checkout {money(summary.total)}
                </Button>
                <p className="text-[11px] text-slate-500">Checkout books one approved withdrawal per deal. Nothing is emailed until you press "Email receipt".</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Receipt just booked */}
        {receipt && (
          <Card className="border-emerald-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base text-emerald-800">Receipt {receipt.id.slice(0, 8).toUpperCase()} — {money(receipt.total)} to {receipt.syndicator_name}</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openPdf(receipt)}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => { setEmailTarget(receipt); setEmailTo(cartData?.syndicator?.email || ''); }}><Mail className="h-4 w-4 mr-1" /> Email receipt</Button>
                <Button size="sm" variant="ghost" onClick={() => setReceipt(null)}>close</Button>
              </div>
            </CardHeader>
            <CardContent>
              <ReceiptLines doc={receipt} />
              {receipt.receipt_emailed_at && <p className="text-xs text-slate-500 mt-2">Emailed to {receipt.receipt_emailed_to} · {new Date(receipt.receipt_emailed_at).toLocaleString()}</p>}
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2">
            <CardTitle className="text-base">Disbursement history{selected ? ` — ${selected.company_name || selected.name}` : ''}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={histFilter.from} onChange={e => setHistFilter({ ...histFilter, from: e.target.value })} className="h-8 w-36" aria-label="from" />
              <span className="text-xs text-slate-500">to</span>
              <Input type="date" value={histFilter.to} onChange={e => setHistFilter({ ...histFilter, to: e.target.value })} className="h-8 w-36" aria-label="to" />
              <Button size="sm" variant="outline" className="h-8" onClick={downloadCsv} disabled={!history.rows.length}><Download className="h-4 w-4 mr-1" /> CSV</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {history.summary && (
              <div className="px-4 pb-2 text-sm text-slate-600">
                {history.summary.count} disbursement{history.summary.count === 1 ? '' : 's'} · paid <b>{money(history.summary.paid_total)}</b>
                {history.summary.voided_total > 0 && <> · voided {money(history.summary.voided_total)}</>}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    {!selected && <TableHead>Syndicator</TableHead>}
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Deals</TableHead>
                    <TableHead>Method / ref</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No disbursements yet.</TableCell></TableRow>}
                  {history.rows.map(d => (
                    <TableRow key={d.id} className={d.status === 'Voided' ? 'opacity-60' : ''} data-testid={`disb-hist-${d.id}`}>
                      <TableCell>{d.paid_on}</TableCell>
                      {!selected && <TableCell><button className="text-blue-600 hover:underline" onClick={() => pick(d.syndicator_id)}>{d.syndicator_name}</button></TableCell>}
                      <TableCell className="text-right font-semibold">{money(d.total)}</TableCell>
                      <TableCell>
                        <details>
                          <summary className="cursor-pointer text-sm text-slate-700">{d.line_count} deal{d.line_count === 1 ? '' : 's'}</summary>
                          <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
                            {d.lines.map(l => <li key={l.position_id}><Link to={`/deals/${l.deal_id}`} className="text-blue-600 hover:underline">{l.deal_number}</Link> {l.deal_name} — {money(l.amount)}</li>)}
                          </ul>
                        </details>
                      </TableCell>
                      <TableCell className="text-sm">{METHOD_LABELS[d.method] || d.method}{d.reference ? ` · ${d.reference}` : ''}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[16rem] truncate" title={d.note}>{d.note || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={d.status === 'Voided' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>{d.status}</Badge>
                        {d.receipt_email_count > 0 && <div className="text-[10px] text-slate-500">emailed ×{d.receipt_email_count}</div>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 px-2" title="Receipt PDF" onClick={() => openPdf(d)}><FileText className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" title="Email receipt" onClick={() => { setEmailTarget(d); setEmailTo(''); }} disabled={d.status === 'Voided'}><Mail className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" title="Void (money back on the positions)" onClick={() => { setVoidTarget(d); setVoidReason(''); }} disabled={d.status === 'Voided'}><Undo2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Confirm checkout */}
        <Dialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm disbursement</DialogTitle>
              <DialogDescription>{confirm && <>{money(confirm.total)} to <b>{confirm.syndicator_name}</b> on {confirm.paid_on} by {METHOD_LABELS[confirm.method] || confirm.method}.</>}</DialogDescription>
            </DialogHeader>
            {confirm && <ReceiptLines doc={confirm} />}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy}>Back</Button>
              <Button onClick={doCheckout} disabled={busy} data-testid="disb-confirm">{busy ? 'Booking…' : 'Book it'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email receipt */}
        <Dialog open={!!emailTarget} onOpenChange={o => !o && setEmailTarget(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Email receipt</DialogTitle><DialogDescription>The PDF receipt is attached. Leave the address blank to use the contact's email on file.</DialogDescription></DialogHeader>
            <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="email on file" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailTarget(null)} disabled={busy}>Cancel</Button>
              <Button onClick={sendEmail} disabled={busy}>{busy ? 'Sending…' : 'Send'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Void */}
        <Dialog open={!!voidTarget} onOpenChange={o => !o && setVoidTarget(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Void this disbursement?</DialogTitle><DialogDescription>{voidTarget && <>{money(voidTarget.total)} goes back onto {voidTarget.syndicator_name}'s positions. The receipt stays in the history marked Voided.</>}</DialogDescription></DialogHeader>
            <Textarea value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={2} placeholder="Reason (required)" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidTarget(null)} disabled={busy}>Cancel</Button>
              <Button variant="destructive" onClick={doVoid} disabled={busy || voidReason.trim().length < 3}>{busy ? 'Voiding…' : 'Void'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
}

function ReceiptLines({ doc }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {doc.lines.map(l => (
          <tr key={l.position_id} className="border-b last:border-0">
            <td className="py-1 pr-2 text-slate-500 w-20">{l.deal_number}</td>
            <td className="py-1 pr-2">{l.deal_name}</td>
            <td className="py-1 text-right font-medium">{money(l.amount)}</td>
          </tr>
        ))}
        <tr><td></td><td className="py-1 font-semibold">Total</td><td className="py-1 text-right font-bold">{money(doc.total)}</td></tr>
      </tbody>
    </table>
  );
}
